import React from 'react';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import cloneDeep from 'lodash/cloneDeep';
import ObjectListWidget from '@plone/volto/components/manage/Widgets/ObjectListWidget';

const messages = defineMessages({
  active: {
    id: 'Active',
    defaultMessage: 'Active',
  },
  addRule: {
    id: 'Add rule',
    defaultMessage: 'Add rule',
  },
  amount: {
    id: 'Amount',
    defaultMessage: 'Amount',
  },
  amountDescription: {
    id: 'Fixed discount amount',
    defaultMessage: 'Fixed discount amount',
  },
  code: {
    id: 'Code',
    defaultMessage: 'Code',
  },
  condition: {
    id: 'Condition',
    defaultMessage: 'Condition',
  },
  conditions: {
    id: 'Conditions',
    defaultMessage: 'Conditions',
  },
  coupon: {
    id: 'Coupon',
    defaultMessage: 'Coupon',
  },
  dateRange: {
    id: 'Date range',
    defaultMessage: 'Date range',
  },
  default: {
    id: 'Default',
    defaultMessage: 'Default',
  },
  delivery: {
    id: 'Delivery',
    defaultMessage: 'Delivery',
  },
  deliveryPickup: {
    id: 'Delivery / pickup',
    defaultMessage: 'Delivery / pickup',
  },
  discountRule: {
    id: 'Discount rule',
    defaultMessage: 'Discount rule',
  },
  discountType: {
    id: 'Discount type',
    defaultMessage: 'Discount type',
  },
  discountValue: {
    id: 'Discount value',
    defaultMessage: 'Discount value',
  },
  fixedAmount: {
    id: 'Fixed amount',
    defaultMessage: 'Fixed amount',
  },
  from: {
    id: 'From',
    defaultMessage: 'From',
  },
  minimumAmount: {
    id: 'Minimum amount',
    defaultMessage: 'Minimum amount',
  },
  mode: {
    id: 'Mode',
    defaultMessage: 'Mode',
  },
  name: {
    id: 'Name',
    defaultMessage: 'Name',
  },
  percentage: {
    id: 'Percentage',
    defaultMessage: 'Percentage',
  },
  percentageDescription: {
    id: 'Use 0.15 for 15%',
    defaultMessage: 'Use 0.15 for 15%',
  },
  pickup: {
    id: 'Pickup',
    defaultMessage: 'Pickup',
  },
  quantity: {
    id: 'Quantity',
    defaultMessage: 'Quantity',
  },
  quantityUse: {
    id: 'Quantity use',
    defaultMessage: 'Quantity use',
  },
  to: {
    id: 'To',
    defaultMessage: 'To',
  },
  type: {
    id: 'Type',
    defaultMessage: 'Type',
  },
  
});

const CONDITION_TYPES = (intl: IntlShape) => [
  ['coupon', intl.formatMessage(messages.coupon)],
  ['daterange', intl.formatMessage(messages.dateRange)],
  ['quantity_use', intl.formatMessage(messages.quantityUse)],
  ['minimum_amount', intl.formatMessage(messages.minimumAmount)],
  ['delivery_pickup', intl.formatMessage(messages.deliveryPickup)],
];

const conditionBaseSchema = (intl: IntlShape) => ({
  title: intl.formatMessage(messages.condition),
  fieldsets: [
    {
      id: 'default',
      title: intl.formatMessage(messages.default),
      fields: ['type'],
    },
  ],
  properties: {
    type: {
      title: intl.formatMessage(messages.type),
      choices: CONDITION_TYPES(intl),
    },
  },
  required: ['type'],
});

const conditionSchemaExtender = (schema: any, data: Record<string, any> = {}, intl: IntlShape) => {
  const next = cloneDeep(schema);

  next.fieldsets[0].fields = ['type'];
  next.required = ['type'];

  switch (data.type) {
    case 'coupon':
      next.title = intl.formatMessage(messages.coupon);
      next.properties.code = {
        title: intl.formatMessage(messages.code),
        type: 'string',
      };
      next.fieldsets[0].fields.push('code');
      next.required.push('code');
      break;

    case 'daterange':
      next.properties.from_datetime = {
        title: intl.formatMessage(messages.from),
        widget: 'datetime',
      };
      next.properties.to_datetime = {
        title: intl.formatMessage(messages.to),
        widget: 'datetime',
      };
      next.fieldsets[0].fields.push('from_datetime', 'to_datetime');
      next.required.push('from_datetime', 'to_datetime');
      break;

    case 'quantity_use':
      next.properties.quantity = {
        title: intl.formatMessage(messages.quantity),
        type: 'integer',
        minimum: 1,
      };
      next.fieldsets[0].fields.push('quantity');
      next.required.push('quantity');
      break;

    case 'minimum_amount':
      next.properties.amount = {
        title: intl.formatMessage(messages.minimumAmount),
        type: 'number',
        minimum: 0,
      };
      next.fieldsets[0].fields.push('amount');
      next.required.push('amount');
      break;

    case 'delivery_pickup':
      next.properties.mode = {
        title: intl.formatMessage(messages.mode),
        choices: [
          ['delivery', intl.formatMessage(messages.delivery)],
          ['pickup', intl.formatMessage(messages.pickup)],
        ],
      };
      next.fieldsets[0].fields.push('mode');
      next.required.push('mode');
      break;

    default:
      break;
  }
  return next;
};

const ruleBaseSchema = (intl: IntlShape) => ({
  title: intl.formatMessage(messages.discountRule),
  addMessage: intl.formatMessage(messages.addRule),
  fieldsets: [
    {
      id: 'default',
      title: intl.formatMessage(messages.default),
      fields: [
        'active',
        'name',
        'discount_type',
        'discount_value',
        'conditions',
      ],
    },
  ],
  properties: {
    active: {
      title: intl.formatMessage(messages.active),
      type: 'boolean',
      default: true,
    },
    name: {
      title: intl.formatMessage(messages.name),
      type: 'string',
    },
    discount_type: {
      title: intl.formatMessage(messages.discountType),
      choices: [
        ['percentage', intl.formatMessage(messages.percentage)],
        ['amount', intl.formatMessage(messages.fixedAmount)],
      ],
    },
    discount_value: {
      title: intl.formatMessage(messages.discountValue),
      type: 'number',
      default: 0,
    },
    conditions: {
      title: intl.formatMessage(messages.conditions),
      widget: 'object_list',
      schema: conditionBaseSchema(intl),
      schemaExtender: conditionSchemaExtender,
    },
  },
  required: ['name', 'discount_type', 'discount_value'],
});

const ruleSchemaExtender = (schema: any, data: Record<string, any> = {}, intl: IntlShape) => {
  const next = cloneDeep(schema);

  if (data.discount_type === 'percentage') {
    next.properties.discount_value = {
      ...next.properties.discount_value,
      title: intl.formatMessage(messages.percentage),
      description: intl.formatMessage(messages.percentageDescription),
      minimum: 0,
      maximum: 1,
    };
  }

  if (data.discount_type === 'amount') {
    next.properties.discount_value = {
      ...next.properties.discount_value,
      title: intl.formatMessage(messages.amount),
      description: intl.formatMessage(messages.amountDescription),
      minimum: 0,
    };
  }

  return next;
};

interface DiscountRulesWidgetProps {
  id: string;
  value?: { items: Array<Record<string, any>> };
  onChange: (id: string, value: { items: Array<Record<string, any>> }) => void;
  [key: string]: any;
}

const DiscountRulesWidget: React.FC<DiscountRulesWidgetProps> = (props) => {
  const { id, value, onChange } = props;
  const intl = useIntl();

  const safeValue = value || { items: [] };
  const items = Array.isArray(safeValue.items) ? safeValue.items : [];

  return (
    <ObjectListWidget
      {...props}
      value={items}
      schema={ruleBaseSchema(intl)}
      schemaExtender={ruleSchemaExtender}
      onChange={(_, nextItems) => {
        onChange(id, {
          ...safeValue,
          items: nextItems,
        });
      }}
    />
  );
};

export default DiscountRulesWidget;
